import { analyticsHelpers, constants } from 'day8-ui';
import { updateSearch } from './querystring';
import {
  getSelectedDestination,
  getSelectedWeeks,
  getYachtAndCabinStatusVariables,
  getAvailableCabinsForWeek,
  getSelectedCrew,
  isSaltEscapes
} from './week';
import { getLeadPackagePricePerPerson, getCabinPriceOnWeekPerPerson } from './pricing';
import { PACKAGES, STANDARD_YACHT, YACHT } from '../constants';

const { TYW_DESTINATION } = constants;

export const onCategorySelect = (standardYacht, location, history, pathname) => history.push({
  pathname,
  search: updateSearch(location, { category: standardYacht.package.id })
});
export const getCategoryList = (standardYachts, destinations, location, cabins, history, pathname, week) => standardYachts && standardYachts
  .sort((a, b) => {
    const aIndex = a.package.orderIndex;
    const bIndex = b.package.orderIndex;
    if (aIndex < 0 || bIndex < 0 || aIndex === bIndex) {
      return 0;
    }
    return aIndex < bIndex ? -1 : 1;
  })
  .map(standardYacht => {
    const crew = getSelectedCrew(destinations, location);
    const weeks = getSelectedWeeks(destinations, location);
    const selectedWeek = week || weeks[0]; // in case we are passing week manually into the function
    const destination = getSelectedDestination(destinations, location) || week?.destination;
    const standardYachtWeeks = (selectedWeek && selectedWeek.standardYachtWeeks) || [];
    const standardYachtWeek = standardYachtWeeks.length && standardYachtWeeks.find(syw => syw.standardYacht.package.id === standardYacht.package.id);
    const availableCabinsForWeek = getAvailableCabinsForWeek(cabins, destinations, location, selectedWeek) || {};
    const hasAvailableCabinsForWeekAndCategory = availableCabinsForWeek && !!Object.keys(availableCabinsForWeek).includes(`${standardYacht.package.id}`);
    const {
      isCabinsUnavailable,
      isCabinsSoldOut,
      isCabinsComingSoon,
      isYachtsUnavailable,
      isYachtsSoldOut,
      isYachtsComingSoon,
      isYachtsOnly,
      isCabinsOnly,
      cabinsHighDemandLimit,
      yachtsHighDemandLimit
    } = getYachtAndCabinStatusVariables(destinations, location, cabins, standardYachtWeek, hasAvailableCabinsForWeekAndCategory, standardYacht.package.id, selectedWeek);

    const minYachtPrice = standardYachtWeek ? getLeadPackagePricePerPerson(standardYachtWeek, selectedWeek, crew) : null;
    const minCabinPrice = getCabinPriceOnWeekPerPerson(cabins, destinations, location, standardYacht.package.id, selectedWeek);
    const isSoldOut = (isCabinsSoldOut && isYachtsSoldOut) || (isCabinsSoldOut && isYachtsUnavailable) || (isYachtsSoldOut && isCabinsUnavailable);
    const priceTiers = (standardYachtWeek && standardYachtWeek.originalPrice !== undefined) && destination.priceTiers !== undefined && Object.values(destination.priceTiers);
    return {
      data: standardYacht,
      price: minYachtPrice || minCabinPrice,
      onClick: () => {
        analyticsHelpers.selectedYachtPackage({
          week: selectedWeek.week,
          financial_year: selectedWeek.year,
          package_name: standardYacht.package.name
        });
        history.push({
          pathname,
          search: updateSearch(location, { category: standardYacht.package.id })
        });
      },
      isUnavailable: isCabinsUnavailable && isYachtsUnavailable,
      isSoldOut,
      isCabinsUnavailable,
      isCabinsSoldOut,
      isCabinsComingSoon,
      isYachtsUnavailable,
      isYachtsSoldOut,
      isYachtsComingSoon,
      isYachtsOnly,
      isCabinsOnly,
      cabinsHighDemandLimit,
      yachtsHighDemandLimit,
      isComingSoon: isCabinsComingSoon && isYachtsComingSoon,
      weekName: selectedWeek.week,
      destinationName: destination.name,
      discount: standardYachtWeek && (standardYachtWeek.discount / standardYacht.maxCapacity),
      isSaltEscapes: isSaltEscapes(destinations, location),
      weekCurrency: selectedWeek.currency,
      priceTiers,
      availableCabinsForWeek,
      hasAvailableCabinsForWeekAndCategory
    };
  });

export const getCabinBookingTypeDescription = (isCabinsComingSoon, destinationId, packageData) => {
  const isBviCnct = destinationId === TYW_DESTINATION.BVI_CNCT;
  if (isCabinsComingSoon) {
    if (destinationId === TYW_DESTINATION.YNF_SALT_ESCAPES_BVI) {
      return 'Book a cabin on a shared yacht, perfect for you and a mate, or partner.';
    }
    return 'Book a full private cabin on a shared yacht, with a mate, or book one shared spot and we will match you with another guest.';
  }
  if (destinationId === TYW_DESTINATION.YNF_SALT_ESCAPES_BVI) {
    return 'Set sail with like-minded adventurers in a cabin on a shared yacht. Perfect for you and a mate, or partner.';
  }
  if (packageData.id === STANDARD_YACHT.PREMIUM_RATING) {
    if (isBviCnct) {
      return 'Set sail with like-minded adventurers in a cabin on a shared yacht. Book a full private cabin with a mate.';
    }
    return 'Set sail with like-minded adventurers in a cabin on a shared yacht. Book a full private cabin with a mate, or one shared spot and we will match you with another guest.';
  }
  if (packageData.id === STANDARD_YACHT.CLASSIC_RATING) {
    return 'Set sail with like-minded adventurers in a cabin on a shared classic yacht. Book with a mate to share a double cabin.';
  }
  if (packageData.id === STANDARD_YACHT.LUXURY_RATING) {
    if (isBviCnct) {
      return 'Set sail with like-minded adventurers in a cabin on a shared yacht. Book with a mate to share a double cabin.';
    }
    return 'Set sail with like-minded adventurers in a cabin on a shared luxury yacht. Book with a mate to share a double cabin.';
  }
  return `Set sail with like-minded adventurers in a cabin on a shared ${packageData.name.toLowerCase()} yacht. Book with a mate to share a double cabin.`;

};

export const getYachtDescription = (packageId, maxGuests, host, length, hullType, hasAircon, suitablePackageIds = []) => {
  // let description =  `Spend an unforgettable week on a yacht that's home to you, up to ${(maxGuests - 1)} of your friends and your very own skipper ${host? 'and host' : ''}.`;
  let description =  `An exclusive range of yachts that can be tailored to your budget and group size. Find sheer sailing pleasure, choose contemporary luxury and define the space you desire. This selection can be modified to suit your needs on the open seas.`;
  if (packageId === PACKAGES.CLASSIC_MONO || suitablePackageIds.includes(PACKAGES.CLASSIC_MONO)) description = 'This non-airconditioned yacht delivers unbeatable value with budget-friendly mooring and fuel costs. Expect a true (and thrilling) sailing experience. Keep in mind that there is limited lounging space compared to catamarans.';
  if (packageId === PACKAGES.CLASSIC_CAT || suitablePackageIds.includes(PACKAGES.CLASSIC_CAT)) description = 'This yacht is a spacious and comfortable choice. It offers plenty of room, dedicated sunbathing areas, and separate cabins for the skipper and host. While it doesn’t have air conditioning, it ensures a smooth and enjoyable sailing experience.';
  if (packageId === PACKAGES.PREMIUM_MONO || suitablePackageIds.includes(PACKAGES.PREMIUM_MONO)) description = 'This yacht offers a fast and smooth sailing experience with the added comfort of air conditioning. It is newer, larger, and has more capacity than the standard model, making it a great choice. Keep in mind that there is limited lounging space compared to catamarans.';
  if (packageId === PACKAGES.PREMIUM_CAT || suitablePackageIds.includes(PACKAGES.PREMIUM_CAT)) description = 'This yacht offers a luxurious and exceptionally comfortable sailing experience. With extra-spacious, air-conditioned interiors, ample sunbathing spots, and dedicated cabins for the crew, it’s simply the definitive Yacht Week experience.';
  if (packageId === PACKAGES.SHARED_TRIPLE_ROOM ) description = `Grab the whole crew and anchor down in our spacious triple rooms, the ultimate headquarters for your Oktoberfest squad. Between sets at the Wiesn, recharge with plush bedding, room service, and a quick turnaround for laundry to keep your group's lederhosen looking fresh. It's the perfect home base to rally the team and prep for the next round of steins in total comfort.`;
  if (packageId === PACKAGES.SHARED_DOUBLE_ROOM ) description = `Swap the deck for a slice of land-based luxury in our spacious double rooms, the ultimate recovery sanctuary for your Oktoberfest takeover. Between legendary sessions at the Wiesn, recharge with plush bedding, room service, and a fresh laundry service to keep your lederhosen looking sharp. Whether you're grabbing a pre-game drink at the bar or crashing after a day of "Prost!", we've got the perfect vibe to keep the party going.`;
  if (packageId === PACKAGES.SHARED_2_PERSON_SUITE ) description = `Level up your Oktoberfest experience in our Junior Suite, the ultimate VIP penthouse vibe featuring separate living and sleeping areas for the whole squad to rally. Blast the AC, refresh in the renovated luxury bath, and keep your festival gear crisp with our daily laundry service before hitting the breakfast buffet to fuel up for the tents. It's the perfect high-end base to pre-game with the mini-bar or crash in style after a legendary day at the Wiesn.`;
  if (packageId === PACKAGES.SINGLE_ROOM ) description = `Retreat to your own private sanctuary after a heavy day at the Wiesn in our cozy single rooms, designed for those who need a high-end spot to recharge and reset. We'll keep your festival fits fresh with laundry service and daily cleaning, while high-speed Wi-Fi and plush bedding ensure you're fully recovered for the next round of steins. It's the ultimate solo base camp to power up before you head back out to the tents.`;
  if (!packageId && +length >= 50 && hullType === YACHT.HULLTYPE_CATAMARAN) description = 'This yacht offers unparalleled luxury, spacious sunbathing areas, a generous galley, and multiple bathrooms for comfort. This lavish experience prioritises comfort and relaxation over high-speed sailing adventures.';
  if (!packageId && +length >= 50 && !hasAircon && hullType === YACHT.HULLTYPE_MONOHULL) description = 'Gather your friends and embark on an unforgettable voyage aboard these spacious monohulls, perfect for hosting a large group of friends. It\'s an excellent choice for value-conscious groups, although keep in mind that air conditioning is not available.';
  return description;
};